import {
  CourierCapabilitiesDto,
  CourierNetworkCapabilityDto,
  KnownCourierCapabilityScope
} from '@groundfloor-platform/mgmt-service-g-sdk';
import {
  mapTransactionTypesToIcons,
  TransactionTypeIcon
} from '../../../deliveries/maps/kiosk-transaction-types-to-icons.map';

export type CourierNetworkCapability = {
  /** Keys the block. Not the name: an entry can be a project, which may share a network's name. */
  networkID: string;
  /** The network's name, shown as "FOR {name}". */
  networkName: string;
  /** What the courier can do in this network, carrier grants and their own together. */
  transactionTypes: TransactionTypeIcon[];
  /** The scope and, where it applies, who holds the grant — folded into tooltip lines. */
  tooltipLines: string[];
};

/**
 * The header's capability blocks, one per network the courier's carrier is granted on.
 *
 * Every entry renders `permissions`, which already has the courier's own grants folded into it —
 * the DTO's top-level `courierPermissions` is attribution only, and showing it separately would
 * double-count. It follows that an empty `networks` renders nothing at all, which is the right
 * answer: a courier whose carrier is granted nowhere can do nothing at any kiosk, however many
 * permissions they hold directly.
 *
 * The server sorts networks by name and permissions alphabetically, so the order is left alone.
 */
export function makeCourierNetworkCapabilities(
  capabilities: CourierCapabilitiesDto | undefined
): CourierNetworkCapability[] {
  if (!capabilities) {
    return [];
  }

  return capabilities.networks.map((entry) => ({
    networkID: entry.network.id,
    // An entry is not always a network — a grant on a project with no network above it stands as its
    // own entry, and then this is the project's name. Either way the name is what there is to show.
    networkName: entry.network.name,
    transactionTypes: mapTransactionTypesToIcons(entry.permissions),
    tooltipLines: makeTooltipLines(entry)
  }));
}

function makeTooltipLines(entry: CourierNetworkCapabilityDto): string[] {
  const lines: string[] = [];

  // `projects` is only populated for the partial scope; a whole-network grant leaves it undefined.
  if (entry.scope === KnownCourierCapabilityScope.Projects) {
    const projects = entry.projects || [];
    lines.push(
      projects.length > 0
        ? `Covers ${projects.length === 1 ? '1 project' : `${projects.length} projects`}: ${projects
            .map((project) => project.name)
            .join(', ')}`
        : 'Covers no projects in this network'
    );
  } else {
    lines.push(`Covers every project in ${entry.network.name}`);
  }

  // Absent when the courier's own carrier holds the grant, which needs no explaining.
  if (entry.inheritedFrom) {
    lines.push(`Granted to ${entry.inheritedFrom.name}`);
  }

  return lines;
}
